import documentReady from '../common/documentReady'
import { formatPrice } from '../common/helpers/number-formatters'
import csrfPost from '../common/helpers/csrfPost'

let previousUnit = 'mm'

let previousLength = ''

let maxLengthForSingles = ''

const idPrefix = (): string => (window.innerWidth < 960) ? '-mobile' : ''

const getLengthField = (): HTMLInputElement => document.getElementById(`customCutting-lengthField${idPrefix()}`) as HTMLInputElement

const getQuantityField = (): HTMLInputElement => document.getElementById(`customCutting-quantityField${idPrefix()}`) as HTMLInputElement

const selectedUnit = (): string => {
  const unitField = document.getElementById(`customCutting-unitField${idPrefix()}`) as HTMLSelectElement
  const selectedUnitOption = unitField.selectedOptions[0]
  return selectedUnitOption.value
}

const getLengthInMm = (): number => {
  let length = parseFloat(getLengthField().value) || 0
  const unit = selectedUnit()
  if (unit === 'm') {
    length *= 1000
  } else if (unit === 'cm') {
    length *= 10
  }
  return length
}

const maxLength = () => document.getElementById('cutting-details').dataset.maxLength

const maxLengthNumber = () => parseInt(maxLength(), 10)

const minLength = () => document.getElementById('cutting-details').dataset.minLength

const minLengthNumber = () => parseInt(minLength(), 10)

const getQuantity = (): number => parseInt(getQuantityField().value, 10)

const getSizeField = (): HTMLSelectElement => document.getElementById(`customCutting-sizeField${idPrefix()}`) as HTMLSelectElement

const getProductId = (): string => getSizeField().value

const validQuantity = (
  quantity: number | typeof NaN
): boolean => !Number.isNaN(quantity) && quantity > 0 && quantity <= 999

const validLength = (length: number): boolean => length >= minLengthNumber() && length <= maxLengthNumber()

// Don't allow overly precise decimals in the length input.
// This replicates the behaviour found in app/javascript/components/CutToSizeGroup/CutToSizeGroups.tsx:225
const stripDecimals = (lengthField: HTMLInputElement): void => {
  const length = lengthField.value
  const countDecimals = (value: (string | number)): number => {
    value = value.toString()
    return (value && value.split('.')[1]?.length) || 0
  }
  const unit = selectedUnit()
  if (unit === 'mm' && countDecimals(length) > 0) {
    lengthField.value = previousLength
  }
  if (unit === 'cm' && countDecimals(length) > 1) {
    lengthField.value = previousLength
  }
  if (unit === 'm' && countDecimals(length) > 3) {
    lengthField.value = previousLength
  }

  previousLength = lengthField.value
}

const resetCustomTubeCuttingBar = (): void => {
  const customCuttingWeight = document.getElementById('customCutting-weight')
  if (customCuttingWeight === null) {
    return
  }

  // Don't reset the form if it was invalid, as it won't have been submitted.
  const length = getLengthInMm()
  const quantity = getQuantity()
  if (!validQuantity(quantity) || !validLength(length)) {
    return
  }

  customCuttingWeight.innerHTML = '0.00'
  document.getElementById('customCutting-weight-mobile').innerHTML = '0.00'

  document.querySelectorAll('.customCutting-lengthField').forEach((el: HTMLInputElement) => { el.value = '' })
  document.querySelectorAll('.customCutting-quantityField').forEach((el: HTMLInputElement) => { el.value = '1' })
  document.querySelectorAll('.customCutting-priceExVat').forEach((el) => { el.innerHTML = '£0.00' })
  document.querySelectorAll('.customCutting-priceIncVat').forEach((el) => { el.innerHTML = '£0.00' })
}

const submitCustomTubeCuttingBar = async (): Promise<void> => {
  const lengthField = getLengthField()
  if (lengthField === null) {
    return
  }

  const length = getLengthInMm()
  const quantity = getQuantity()

  if (!validQuantity(quantity) || !validLength(length)) {
    return
  }

  const data = {
    size: { 0: getProductId() },
    length: { 0: length },
    quantity: { 0: quantity }
  }
  await csrfPost('/galvanised-tubes/add-to-basket', data)
}

const recalculate = (): void => {
  const sizeField = getSizeField()
  const selectedSizeOption = sizeField.selectedOptions[0]
  const pricePerM = parseFloat(selectedSizeOption.dataset.price)
  const weightPerM = parseFloat(selectedSizeOption.dataset.weight)
  const lengthField = getLengthField()
  const quantityField = getQuantityField()

  const invalidField = 'customCutting-invalidField'

  stripDecimals(lengthField)

  let length = getLengthInMm()
  if (validLength(length)) {
    lengthField.classList.remove(invalidField)
  } else {
    lengthField.classList.add(invalidField)
    length = 0
  }

  let quantity = getQuantity()
  if (validQuantity(quantity)) {
    quantityField.classList.remove(invalidField)
  } else {
    quantityField.classList.add(invalidField)
    quantity = 0
  }

  const priceExVat = (length * pricePerM * quantity) / 1000
  const priceIncVat = priceExVat * 1.2
  const weight = (length * weightPerM * quantity) / 1000
  document.querySelectorAll('.customCutting-priceExVat').forEach((el) => { el.innerHTML = formatPrice(priceExVat) })
  document.querySelectorAll('.customCutting-priceIncVat').forEach((el) => { el.innerHTML = formatPrice(priceIncVat) })
  document.getElementById('customCutting-weight').innerHTML = weight.toFixed(2)
  document.getElementById('customCutting-weight-mobile').innerHTML = weight.toFixed(2)

  calculateOriginalPrice(selectedSizeOption, length, quantity)
}

const calculateOriginalPrice = (selectedSizeOption: HTMLOptionElement, length: number, quantity: number): void => {
  if (selectedSizeOption.dataset.originalPrice) {
    const originalPricePerM = parseFloat(selectedSizeOption.dataset.originalPrice)
    const originalPriceExVat = (length * originalPricePerM * quantity) / 1000
    document.querySelectorAll('.customCutting-originalPriceExVat').forEach((el) => { el.innerHTML = formatPrice(originalPriceExVat) })
    document.querySelectorAll<HTMLElement>('.customCutting-originalPrice').forEach((el) => { el.style.display = 'revert' })
  } else {
    document.querySelectorAll('.customCutting-originalPriceExVat').forEach((el) => { el.innerHTML = '£0.00' })
    document.querySelectorAll<HTMLElement>('.customCutting-originalPrice').forEach((el) => { el.style.display = 'none' })
  }
}

const changeUnit = (): void => {
  const lengthField = getLengthField()
  let length = parseFloat(lengthField.value) || 0
  const unit = selectedUnit()

  if (unit === 'mm') {
    if (previousUnit === 'm') {
      length *= 1000
    } else if (previousUnit === 'cm') {
      length *= 10
    }
    lengthField.min = minLength()
    lengthField.max = maxLength()
    lengthField.step = '1'
    lengthField.value = length.toFixed(0)
  } else if (unit === 'm') {
    if (previousUnit === 'mm') {
      length /= 1000
    } else if (previousUnit === 'cm') {
      length /= 100
    }
    lengthField.min = (minLengthNumber() / 1000).toFixed(3)
    lengthField.max = (maxLengthNumber() / 1000).toFixed(3)
    lengthField.step = '0.001'
    lengthField.value = length.toFixed(3)
  } else if (unit === 'cm') {
    if (previousUnit === 'm') {
      length *= 100
    } else if (previousUnit === 'mm') {
      length /= 10
    }
    lengthField.min = (minLengthNumber() / 10).toFixed(1)
    lengthField.max = (maxLengthNumber() / 10).toFixed(1)
    lengthField.step = '0.1'
    lengthField.value = length.toFixed(1)
  }

  previousUnit = unit
}

const bundleSwap = (): void => {
  const bundleSelect = document.getElementById(`customCutting-bundleSelect${idPrefix()}`) as HTMLSelectElement
  const selected = bundleSelect.value
  const sizeOptions = getSizeField().options
  if (selected === 'bundles') {
    document.getElementById('cutting-details').dataset.maxLength = '6400'
    getLengthField().max = '6400'
    setDatasets(bundleSelect.dataset.bundlesInfo, sizeOptions, 'bundles')
  } else {
    document.getElementById('cutting-details').dataset.maxLength = maxLengthForSingles
    getLengthField().max = maxLengthForSingles
    setDatasets(bundleSelect.dataset.productsInfo, sizeOptions, 'singles')
  }
  recalculate()
}

const setDatasets = (info: string, sizeOptions: HTMLOptionsCollection, selectionType: 'bundles' | 'singles'): void => {
  const { ids, prices, originalPrices, weights, sizes } = JSON.parse(info)
  const foundOptions = []
  ids.forEach((id: number, index: number) => {
    const size = sizes[index]
    const sizeOption = Array.from(sizeOptions).find(option => option.dataset.originalText === size)
    if (sizeOption) {
      foundOptions.push(sizeOption)
      sizeOption.value = id.toString()
      sizeOption.dataset.price = prices[index]
      sizeOption.dataset.originalPrice = originalPrices[index] || ''
      sizeOption.dataset.weight = weights[index]
      const { originalText } = sizeOption.dataset
      if (selectionType === 'bundles') {
        const lengthsPerSize = {
          '26.9 mm': 127,
          '33.7 mm': 91,
          '42.4 mm': 61,
          '48.3 mm': 61,
          '60.3 mm': 37
        }
        const lengthsDescription = `(${lengthsPerSize[originalText]} lengths)`
        sizeOption.textContent = `${originalText} ${lengthsDescription}`
      } else {
        sizeOption.textContent = originalText
      }
    }
  })

  Array.from(sizeOptions).forEach((option) => {
    if (foundOptions.includes(option)) {
      option.disabled = false
    } else {
      if (option.selected) {
        option.selected = false
        foundOptions[0].selected = true
      }
      option.disabled = true
    }
  })
}

const customTubeCuttingBar = (): void => {
  const sizeField = document.getElementById('customCutting-sizeField') as HTMLSelectElement
  if (sizeField === null) {
    return
  }
  maxLengthForSingles = maxLength()

  document.getElementById('customCutting-sizeField').addEventListener('change', recalculate)
  document.getElementById('customCutting-lengthField').addEventListener('input', recalculate)
  document.getElementById('customCutting-unitField').addEventListener('change', changeUnit)
  document.getElementById('customCutting-quantityField').addEventListener('input', recalculate)

  document.getElementById('customCutting-sizeField-mobile').addEventListener('change', recalculate)
  document.getElementById('customCutting-lengthField-mobile').addEventListener('input', recalculate)
  document.getElementById('customCutting-unitField-mobile').addEventListener('change', changeUnit)
  document.getElementById('customCutting-quantityField-mobile').addEventListener('input', recalculate)

  const bundleSelect = document.getElementById('customCutting-bundleSelect')
  if (bundleSelect !== null) {
    bundleSelect.addEventListener('change', bundleSwap)
    document.getElementById('customCutting-bundleSelect-mobile').addEventListener('change', bundleSwap)
  }

  const { diameter } = document.getElementById('cutting-details').dataset

  if (diameter !== '') {
    document.querySelectorAll('.customCutting-sizeField option').forEach((sizeOption: HTMLOptionElement) => {
      if (sizeOption.textContent.indexOf(diameter) !== -1) {
        sizeOption.selected = true
      }
    })
  }

  const selectedSizeOption = sizeField.selectedOptions[0]
  calculateOriginalPrice(selectedSizeOption, 0, 0)
}

documentReady(customTubeCuttingBar)

export { resetCustomTubeCuttingBar, submitCustomTubeCuttingBar }
